import { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, ArrowLeft, ArrowRight, Check, ArrowRight as ArrowRightIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { UploadedFile, SessionMeta, assignTakeNumbers, buildFileName, sanitize } from "@/lib/tape";
import { VideoTrimmer } from "./VideoTrimmer";
import { preloadFFmpeg } from "@/lib/ffmpeg-convert";
import { logError } from "@/lib/logger";


interface Props {
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  meta: SessionMeta;
  setMeta: React.Dispatch<React.SetStateAction<SessionMeta>>;
  onBack: () => void;
  onContinue: () => void;
}

export function ReviewStage({
  files,
  setFiles,
  meta,
  setMeta,
  onBack,
  onContinue,
}: Props) {
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState(false);
  const [detectedFromSlate, setDetectedFromSlate] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      abortRef.current?.abort();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setFiles((prev) => assignTakeNumbers(prev));
  }, [setFiles]);

  // Warm up ffmpeg core during idle time so the first export feels instant.
  useEffect(() => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const idle = w.requestIdleCallback;
    if (idle) {
      idle(() => preloadFFmpeg(), { timeout: 2000 });
    } else {
      const t = setTimeout(() => preloadFFmpeg(), 800);
      return () => clearTimeout(t);
    }
  }, []);

  async function detectRole() {
    if (transcribing) return; // prevent duplicate concurrent requests
    const slate = files.find((f) => f.isSlate);
    if (!slate) {
      toast.error("No slate available — mark a video as Slate first");
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    let timedOut = false;
    timeoutRef.current = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 10000);

    setTranscribing(true);
    try {
      let videoUrl = slate.remoteUrl;
      // Lazily upload only the slate (small) so the AI function can fetch it.
      if (!videoUrl) {
        const { data: auth } = await supabase.auth.getUser();
        const uid = auth.user?.id;
        if (!uid) throw new Error("You must be signed in to detect the role");
        const ext = slate.file.name.split(".").pop() || "mp4";
        const path = `${uid}/slate-${slate.id}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("uploads")
          .upload(path, slate.file, {
            contentType: slate.file.type || "video/mp4",
            upsert: true,
          });
        if (upErr) throw upErr;
        const { data: signed, error: signErr } = await supabase.storage
          .from("uploads")
          .createSignedUrl(path, 60 * 10);
        if (signErr || !signed) throw signErr ?? new Error("Could not sign URL");
        videoUrl = signed.signedUrl;
        if (unmountedRef.current || controller.signal.aborted) return;
        setFiles((prev) =>
          prev.map((f) =>
            f.id === slate.id
              ? { ...f, remoteUrl: videoUrl, storagePath: path }
              : f,
          ),
        );
      }
      const { data, error } = await supabase.functions.invoke(
        "transcribe-slate",
        { body: { videoUrl }, signal: controller.signal as AbortSignal },
      );
      if (controller.signal.aborted || unmountedRef.current) return;
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setTranscript(data?.transcript ?? "");
      if (data?.roleName) {
        setMeta((m) => ({ ...m, roleName: data.roleName }));
        setDetectedFromSlate(true);
        setEditingRole(false);
        toast.success("Role detected from slate.");
      } else {
        toast.message("Couldn't detect a role from this slate.", {
          description: data?.transcript
            ? `Heard: "${data.transcript.slice(0, 80)}..."`
            : undefined,
        });
        setEditingRole(true);
      }
    } catch (e) {
      if (unmountedRef.current) return;
      logError("transcribe-slate", e);
      if (timedOut) {
        setEditingRole(true);
        toast.error("No role name detected. Please enter it manually.");
      } else {
        toast.error("Couldn't detect a role from this slate.");
      }
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      abortRef.current = null;
      if (!unmountedRef.current) setTranscribing(false);
    }
  }

  function updateFile(id: string, patch: Partial<UploadedFile>) {
    setFiles((prev) =>
      assignTakeNumbers(
        prev.map((f) => {
          if (f.id !== id) return f;
          return { ...f, ...patch };
        }),
      ),
    );
  }

  const roleDetected = detectedFromSlate && !!meta.roleName && !editingRole;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
          Review &amp; trim
        </h1>
        <p className="text-muted-foreground mt-2 text-base">
          Confirm your details, trim each clip, and preview the final filenames.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* LEFT — Metadata (40%) */}
        <aside className="lg:col-span-2">
          <div className="lg:sticky lg:top-24 space-y-4">
            <Card className="p-6 bg-card border-border space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-base">Details</h2>
                {transcribing && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Loader2 className="size-3 animate-spin" />
                    Transcribing slate…
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName" className="text-xs text-muted-foreground">
                    First name
                  </Label>
                  <Input
                    id="firstName"
                    value={meta.firstName}
                    onChange={(e) =>
                      setMeta((m) => ({ ...m, firstName: e.target.value }))
                    }
                    placeholder="Oskar"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName" className="text-xs text-muted-foreground">
                    Last name
                  </Label>
                  <Input
                    id="lastName"
                    value={meta.lastName}
                    onChange={(e) =>
                      setMeta((m) => ({ ...m, lastName: e.target.value }))
                    }
                    placeholder="Fromm"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="projectName" className="text-xs text-muted-foreground">
                  Project name
                </Label>
                <Input
                  id="projectName"
                  value={meta.projectName}
                  onChange={(e) =>
                    setMeta((m) => ({ ...m, projectName: e.target.value }))
                  }
                  placeholder="ProjectX"
                />
              </div>

              {/* Role with detection feedback */}
              <div className="space-y-1.5">
                <Label htmlFor="roleName" className="text-xs text-muted-foreground">
                  Role name
                </Label>
                {roleDetected ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-success/30 bg-success/10 px-3 py-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Check className="size-4 text-success shrink-0" strokeWidth={3} />
                      <span className="text-sm">
                        <span className="text-muted-foreground">Detected role:</span>{" "}
                        <span className="font-medium text-foreground">{meta.roleName}</span>
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditingRole(true)}
                      className="text-xs text-primary hover:underline shrink-0"
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <Input
                    id="roleName"
                    autoFocus={editingRole}
                    value={meta.roleName}
                    onChange={(e) => {
                      setMeta((m) => ({ ...m, roleName: e.target.value }));
                      if (detectedFromSlate) setDetectedFromSlate(false);
                    }}
                    onBlur={() => meta.roleName && setEditingRole(false)}
                    placeholder="John"
                  />
                )}
              </div>

              {transcript && (
                <p className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3 border border-border leading-relaxed">
                  <span className="text-foreground/70 font-medium">Slate transcript:</span> "{transcript}"
                </p>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={detectRole}
                disabled={transcribing}
                className="w-full"
              >
                {transcribing ? (
                  <>
                    <Loader2 className="size-3.5 mr-2 animate-spin" />
                    Detecting…
                  </>
                ) : (
                  <>
                    <Sparkles className="size-3.5 mr-2" />
                    Detect role from slate
                  </>
                )}
              </Button>
            </Card>
          </div>
        </aside>

        {/* RIGHT — Video workspace (60%) */}
        <section className="lg:col-span-3 space-y-6">
          <div className="flex items-baseline justify-between">
            <h2 className="font-semibold text-base">Clips</h2>
            <span className="text-xs text-muted-foreground">
              {files.length} clip{files.length === 1 ? "" : "s"}
            </span>
          </div>

          {files.map((f, idx) => {
            const defaultLabel = `Scene_${idx + 1}`;
            const currentLabel = f.label ?? defaultLabel;
            const displayName = buildFileName(meta, { ...f, label: currentLabel });
            const prefix = [
              sanitize(meta.firstName) || "First",
              sanitize(meta.lastName) || "Last",
              sanitize(meta.projectName) || "Project",
              sanitize(meta.roleName) || "Role",
            ].join("_") + "_";

            return (
              <Card key={f.id} className="p-5 md:p-6 bg-card border-border space-y-5">
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3 min-w-0">
                    <p className="text-xs text-muted-foreground truncate font-mono min-w-0 flex-1">
                      {f.name}
                    </p>
                    {f.isSlate && (
                      <span className="shrink-0 inline-flex items-center rounded-full bg-primary/15 text-primary border border-primary/20 px-2.5 h-6 text-[11px] font-medium">
                        Slate
                      </span>
                    )}
                  </div>

                  <Label className="text-xs text-muted-foreground">Filename</Label>
                  <div className="flex items-stretch rounded-md border border-input bg-background overflow-hidden focus-within:ring-1 focus-within:ring-ring">
                    <span className="pl-3 pr-0 flex items-center font-mono text-xs text-muted-foreground whitespace-nowrap select-none">
                      {prefix}
                    </span>
                    <Input
                      value={currentLabel}
                      onChange={(e) => {
                        const normalized = e.target.value.replace(/\s+/g, "_");
                        updateFile(f.id, { label: normalized });
                      }}
                      placeholder={defaultLabel}
                      className="h-9 border-0 rounded-none shadow-none focus-visible:ring-0 px-1 font-mono text-xs"
                    />
                    <span className="pr-3 flex items-center font-mono text-xs text-muted-foreground select-none">
                      .mp4
                    </span>
                  </div>
                </div>

                <VideoTrimmer
                  file={f}
                  onChange={(patch) => updateFile(f.id, patch)}
                />

                {/* Filename transformation preview */}
                <div className="rounded-lg border border-border bg-background/50 p-3.5">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 text-sm">
                    <span className="font-mono text-xs text-muted-foreground truncate sm:max-w-[40%]">
                      {f.name}
                    </span>
                    <ArrowRightIcon className="size-3.5 text-muted-foreground shrink-0" />
                    <span className="font-mono text-xs text-primary font-medium break-all sm:truncate sm:flex-1">
                      {displayName}
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </section>
      </div>

      <div className="flex justify-between gap-3 pt-2">
        <Button variant="outline" size="lg" onClick={onBack}>
          <ArrowLeft className="size-4 mr-2" />
          Back
        </Button>
        <Button
          size="lg"
          onClick={onContinue}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Continue to export
          <ArrowRight className="size-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}


